// src/pages/admin/RequestsAdmin.jsx
import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  bulkUpdateRequests,
  getRequests,
  updateRequestStatus,
} from "../../api/requests";
import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

export default function RequestsAdmin() {
  const [filterId, setFilterId] = useState("");

  // ─── Bulk-selection & sorting state ────────────────────────────────────
  const [selected, setSelected] = useState([]); // IDs of selected rows
  const selectAllRef = useRef(); // header checkbox ref
  const [bulkStatus, setBulkStatus] = useState("open"); // status to apply in bulk

  const [sortBy, setSortBy] = useState("createdAt"); // which column
  const [sortOrder, setSortOrder] = useState("desc"); // asc | desc

  const queryClient = useQueryClient();

  // ─── Fetch requests (refetch whenever filterId changes) ────────────────
  const {
    data: requests = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["adminRequests", filterId],
    queryFn: () => getRequests(filterId.trim()),
  });

  // ─── Single-update mutation ──────────────────────────────────────────────
  const statusMutation = useMutation({
    mutationFn: ({ requestId, newStatus }) =>
      updateRequestStatus(requestId, newStatus),
    onSuccess: ({ status }) => {
      toast.success(`✔️ Request set to "${status}"`);
      queryClient.invalidateQueries({ queryKey: ["adminRequests", filterId] });
    },
  });

  // ─── Bulk-update mutation ───────────────────────────────────────────────
  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, newStatus }) => bulkUpdateRequests(ids, newStatus),
    onSuccess: ({ modifiedCount, status }) => {
      toast.success(`✔️ ${modifiedCount} requests set to "${status}"`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ["adminRequests", filterId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "❌ Bulk update failed");
    },
  });

  // ─── Keep header checkbox indeterminate when some rows are checked ───────
  useEffect(() => {
    if (!selectAllRef.current) return;
    const all = requests.length;
    const sel = selected.length;
    selectAllRef.current.indeterminate = sel > 0 && sel < all;
  }, [selected, requests]);

  // ─── Handle sort clicks ────────────────────────────────────────────────
  const requestSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  // ─── Apply sorting & then render ───────────────────────────────────────
  const sortedRequests = [...requests].sort((a, b) => {
    let av = a[sortBy],
      bv = b[sortBy];
    // if dates, compare as Date:
    if (sortBy === "createdAt") {
      av = new Date(av).getTime();
      bv = new Date(bv).getTime();
    }
    if (av < bv) return sortOrder === "asc" ? -1 : 1;
    if (av > bv) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Contact Requests</h2>

      {/* ─── Search bar ────────────────────────────────────────────────────── */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by Request ID…"
          value={filterId}
          onChange={(e) => setFilterId(e.target.value)}
          className="text-primary px-3 py-2 border rounded-lg w-64"
        />
      </div>

      {isLoading ? (
        <p>Loading…</p>
      ) : isError ? (
        <p className="text-red-600">Error loading requests.</p>
      ) : (
        <>
          {/* ─── Bulk Actions Bar ────────────────────────────────────────── */}
          {selected.length > 1 && (
            <div className="flex items-center gap-2 mb-4 bg-surface p-3 rounded">
              <span className="text-sm text-gray-200">
                {selected.length} selected
              </span>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="px-2 py-1 rounded bg-gray-700 text-white"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={() =>
                  bulkStatusMutation.mutate({
                    ids: selected,
                    newStatus: bulkStatus,
                  })
                }
                disabled={bulkStatusMutation.isLoading}
                className="px-3 py-1 bg-accent text-white rounded hover:opacity-90"
              >
                {bulkStatusMutation.isLoading ? "Updating…" : "Set Status"}
              </button>
            </div>
          )}

          <div className="overflow-x-auto bg-surface rounded-lg shadow py-5 mb-6">
            <table className="w-full table-auto text-center">
              <thead className="bg-surface sticky top-0">
                <tr>
                  {/* Select-all checkbox */}
                  <th className="px-2 py-1">
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={
                        selected.length === requests.length &&
                        requests.length > 0
                      }
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? requests.map((r) => r.requestId)
                            : []
                        )
                      }
                    />
                  </th>

                  {[
                    { label: "Request ID", key: "requestId" },
                    { label: "Date & Time", key: "createdAt" },
                    { label: "Name", key: "fullName" },
                    { label: "Email", key: "email" },
                    { label: "Vehicle", key: "regNumber" },
                    { label: "Payment", key: "paymentOption" },
                    { label: "Status", key: "status" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      className="text-sm px-2 py-1 cursor-pointer select-none text-white"
                      onClick={() => requestSort(col.key)}
                    >
                      {col.label}{" "}
                      <SortIcon
                        columnKey={col.key}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                      />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {sortedRequests.map((r) => (
                  <tr key={r._id}>
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={selected.includes(r.requestId)}
                        onChange={() =>
                          setSelected((s) =>
                            s.includes(r.requestId)
                              ? s.filter((id) => id !== r.requestId)
                              : [...s, r.requestId]
                          )
                        }
                      />
                    </td>
                    <td className="text-sm px-2 py-1 font-mono">{r.requestId}</td>
                    <td className="text-sm px-2 py-1 text-white/70">
                      {new Date(r.createdAt).toUTCString()}
                    </td>
                    <td className="text-sm px-2 py-1">{r.fullName}</td>
                    <td className="text-sm px-2 py-1">{r.email}</td>
                    <td className="text-sm px-2 py-1">{r.regNumber || "N/A"}</td>
                    <td className="text-sm px-2 py-1">{r.paymentOption || "N/A"}</td>
                    <td className="text-sm px-2 py-1">
                      <select
                        value={r.status}
                        onChange={(e) =>
                          statusMutation.mutate({
                            requestId: r.requestId,
                            newStatus: e.target.value,
                          })
                        }
                        className="text-accent border rounded"
                        disabled={statusMutation.isLoading}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-4 text-brandAccent">
                      No requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Small helper to render the up/down arrows ─────────────────────────
function SortIcon({ columnKey, sortBy, sortOrder }) {
  if (sortBy !== columnKey) {
    return (
      <ChevronUpDownIcon className="inline h-4 w-4 text-secondary-accent" />
    );
  }
  return sortOrder === "asc" ? (
    <ChevronUpIcon className="inline h-4 w-4 text-secondary" />
  ) : (
    <ChevronDownIcon className="inline h-4 w-4 text-secondary" />
  );
}
